import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, MAX_FILTER_OPTIONS_COUNT } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet, getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import {
    isFacetOptionCheckedForSemanticUrl,
    isFlatFacetOptionCheckedForCategoryIdUrl,
} from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

export const StyleAndPeriodFilter = () => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const styleFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.STYLE_PERIOD_ID));
    const rawFacets = useAppSelector(getSearchFacets);
    const { options = [] } = styleFacet;

    const isOptionChecked = useCallback(
        (option: FacetOption) => {
            const checkedFromLegacyCategoryId = isFlatFacetOptionCheckedForCategoryIdUrl(
                queryParams as Record<string, unknown>,
                option.id,
                FacetTypes.STYLE_PERIOD_ID,
                rawFacets,
                option.selected
            );
            return isFacetOptionCheckedForSemanticUrl(
                queryParams as Record<string, unknown>,
                FacetTypes.STYLE_PERIOD_ID,
                option,
                checkedFromLegacyCategoryId
            );
        },
        [queryParams, rawFacets]
    );

    const onStyleOptionClick = (option: FacetOption) => {
        const newCheckStatus = !isOptionChecked(option);
        const semanticValue = urlSafeTitle(option.name ?? String(option.id));

        let newParams = modifyParamsWhenChecked(
            queryParams,
            FacetTypes.STYLE_PERIOD_ID,
            semanticValue,
            newCheckStatus,
            {
                useSemanticAttributeParams: true,
            }
        );

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            browseFacetOptionRemovedId: newCheckStatus ? undefined : option.id,
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.StylePeriodChanged : FilterAction.StylePeriodRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(() => {
        const sortedOptions = options
            .filter((option) => option.count !== 0 || isOptionChecked(option))
            .sort((a, b) => b.count - a.count);
        if (!isExpanded) {
            return sortedOptions.slice(0, MAX_FILTER_OPTIONS_COUNT);
        }
        return sortedOptions;
    }, [isExpanded, options, isOptionChecked]);

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'search_facet_title_stylePeriod' })}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="#fff"
                onChange={(isExpanded) => setIsExpanded(isExpanded)}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {filteredOptions.map((option) => (
                        <Checkbox.Option
                            checked={isOptionChecked(option)}
                            disabled={option.count === 0 && !isOptionChecked(option)}
                            key={option.id}
                            onChange={() => onStyleOptionClick(option)}
                        >
                            {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                        </Checkbox.Option>
                    ))}
                </div>
            </ViewMore>
        </Accordion>
    );
};
